import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  orderDetailsSelector,
  queryTextSelector,
} from '../../selectors/bundleSelectors';
import {
  authTokenSelector,
  userRoleSelector,
} from '../../selectors/userSelectors';
import {
  fetchActionHistory,
  getEmailAccountsInfo,
  getEntitySuspensionDetails,
} from '../../actions/orderActions';
import { FormattedMessage, useIntl } from 'react-intl';
import ProgressBar from '../progressBar';
import { ReactComponent as NoMailContentLogo } from '../../assets/no-mail-content.svg';
import { capitalize, ENTITY_TYPE, getAccountIdFromQuery, isAccountIdSearchQuery, isRoleTitan } from '../utils';
import { getFormattedDate } from '../../utils/helper';
import styles from './BundleDetailsEmailInfo.module.scss';
import ManageButton from '../manageButton';
import medusa from '../../utils/medusa';
import { toast } from 'react-toastify';
import CustomButton from '../Common/Button/Button';
import { TEmailAccountInfo } from '../../types/emailAccountsInfo.types';
import { ReactComponent as LoadingSpinner } from '../../assets/loading-spinner.svg';
import { getLatestMailOrder } from '../../helpers/product.helpers';
import classNames from 'classnames';
import { BUNDLE_STATUSES } from '../../helpers/constants';
import { parseEmailToUtf8FromRecord } from '../../helpers/idn.helpers';

interface BundleDetailsEmailInfoProps {
  mailOrderIds?: Array<number>;
  renderStatusIndicator: (
    status: string,
    entityType: ENTITY_TYPE,
    options: Object
  ) => JSX.Element;
  handleSuspendUnsuspendEmailAccount: (
    accountId: number,
    buttonName: string
  ) => void;
  refreshEmailInfo: number; // epoch millis used to refresh the component
}

const BundleDetailsEmailInfo = ({
  mailOrderIds,
  renderStatusIndicator,
  handleSuspendUnsuspendEmailAccount,
  refreshEmailInfo,
}: BundleDetailsEmailInfoProps) => {
  const dispatch = useDispatch();
  const [isAccountHistoryFetching, setIsAccountHistoryFetching] =
    useState<boolean>(false);
  const [isAccountsInfoFetching, setIsAccountsInfoFetching] =
    useState<boolean>(true);
  const queryText = useSelector(queryTextSelector);
  const token = useSelector(authTokenSelector);
  const userRole = useSelector(userRoleSelector);
  const isTitanRole = isRoleTitan(userRole);

  const orders = useSelector(orderDetailsSelector);

  const [emailAccountsInfo, setEmailAccountsInfo] =
    useState<Array<TEmailAccountInfo> | null>(null);

  const intl = useIntl();

  useEffect(() => {
    const fetchEmailAccountsInfo = async () => {
      const mailOrder = getLatestMailOrder(orders);

      if (!mailOrder) {
        setIsAccountsInfoFetching(false);
        return;
      }

      setIsAccountsInfoFetching(true);
      try {
        const payload = {
          // @deprecated - mailOrderIds to be removed
          mailOrderIds: mailOrderIds,
          ...(mailOrderIds?.[0] ? { mailOrderId: mailOrderIds?.[0] } : {}),
          ...(
            isAccountIdSearchQuery(queryText)
              ? { accountId: getAccountIdFromQuery(queryText) }
              : { email: queryText }
          )
        };
        const accounts: any = await dispatch(
          getEmailAccountsInfo(payload, token)
        );

        setEmailAccountsInfo(
          (accounts as Array<TEmailAccountInfo>).map((item) => ({
            ...item,
            account: parseEmailToUtf8FromRecord(item.account),
          }))
        );
      } catch (err) {
        toast.error(
          'Error fetching email accounts information. Please try again.'
        );
        console.log(err);
      } finally {
        setIsAccountsInfoFetching(false);
      }
    };
    //
    fetchEmailAccountsInfo();
  }, [dispatch, refreshEmailInfo]);

  useEffect(() => {
    const fetchAccountsSuspensions = () => {
      if (emailAccountsInfo && emailAccountsInfo.length) {
        const accountIds: Array<number> = [];
        emailAccountsInfo.forEach((info: any) => {
          accountIds.push(info.account.id);
        });

        dispatch(
          getEntitySuspensionDetails(
            accountIds,
            ENTITY_TYPE.ACCOUNT.toUpperCase(),
            token
          )
        );
      }
    };

    fetchAccountsSuspensions();
  }, [emailAccountsInfo]);

  const markAccountAsSafe = async (accountId: number) => {
    await medusa.markMailAccountSafe(accountId);
    return toast.info('Account has been marked as safe');
  };

  const handleAccountHistoryClicked = async (accountId: number) => {
    try {
      setIsAccountHistoryFetching(true);
      await dispatch(
        fetchActionHistory(null, accountId, ENTITY_TYPE.ACCOUNT, token)
      );
    } catch (e) {
      toast.error('Error fetching account history');
      console.log(e);
    } finally {
      setIsAccountHistoryFetching(false);
    }
  };

  const getEmailInfoToDisplay = useCallback(() => {
    if (emailAccountsInfo?.length) {
      return emailAccountsInfo.map((info: any) => {
        const account = info.account;
        const accountStatus = capitalize(account.status);
        const isDeletedAccount =
          accountStatus.toLowerCase() === BUNDLE_STATUSES.DELETED;

        const usageInfo = account?.storageUsedInMb || 0;
        const quota = account?.storageLimitInMb ?? 0;
        const usagePercent = quota > 0 ? (usageInfo * 100) / quota + '%' : '0%';
        const usageSummary = `${usageInfo} MB / ${quota} MB`;

        const manageButtonAccountOptions = [];

        // suspend will always show as we can add multiple suspension types / reasons
        manageButtonAccountOptions.push({
          title: intl.formatMessage({ id: 'suspend-account' }),
          buttonName: 'suspendAccount',
          onClick: () =>
            handleSuspendUnsuspendEmailAccount(account.id, 'suspendAccount'),
        });
        if (isTitanRole) {
          manageButtonAccountOptions.push({
            title: intl.formatMessage({ id: 'mark-account-safe' }),
            buttonName: 'markAccountSafe',
            onClick: () => markAccountAsSafe(account.id),
          });
        }
        if (accountStatus === 'Suspended') {
          manageButtonAccountOptions.push({
            title: intl.formatMessage({ id: 'unsuspend-account' }),
            buttonName: 'unsuspendAccount',
            onClick: () =>
              handleSuspendUnsuspendEmailAccount(
                account.id,
                'unsuspendAccount'
              ),
          });
        }

        const manageButtonAccountProp = [
          {
            header: intl.formatMessage({ id: 'manage-account' }),
            buttons: manageButtonAccountOptions,
          },
        ];

        return (
          <div key={account.id} className={styles.emailInfoWrapper}>
            <div>
              <div className={styles.itemRow}>
                <div>Account ID</div>
                <div>{account.id}</div>
              </div>

              <div className={styles.itemRow}>
                <div>Email</div>
                <div>{account.email}</div>
              </div>

              <div className={styles.itemRow}>
                <div>Mail Order ID</div>
                <div>{account.orderId}</div>
              </div>

              {account.planId ? (
                <div className={styles.itemRow}>
                  <div>
                    <FormattedMessage id="order-details-plan-details" />
                  </div>
                  <div>
                    {(account.planDisplayName || '').toUpperCase() || '-'} |{' '}
                    {account.planId} | {account.planType || '-'}
                  </div>
                </div>
              ) : null}

              <div className={styles.itemRow}>
                <div>Status</div>
                <div>
                  {renderStatusIndicator(accountStatus, ENTITY_TYPE.ACCOUNT, {
                    accountId: account.id,
                  })}
                </div>
              </div>

              <div className={styles.itemRow}>
                <div>Account Type</div>
                <div>{account.accountType || '-'}</div>
              </div>

              <div className={styles.itemRow}>
                <div>Plan Type</div>
                <div>{account.plan?.planType || '-'}</div>
              </div>

              <div className={styles.itemRow}>
                <div>Created At</div>
                <div>{getFormattedDate(account.createdAt / 1000)}</div>
              </div>

              <div className={styles.itemRow}>
                <div>Quota</div>
                <div>
                  {usageInfo > 0 && (
                    <div>
                      <ProgressBar
                        style={{
                          width: `max(${usagePercent}, 2%)`,
                          height: '4px',
                        }}
                      />
                    </div>
                  )}
                  <div>{usageSummary}</div>
                </div>
              </div>

              <div className="flex account-info-buttons-container">
                {!isDeletedAccount && (
                  <ManageButton
                    optionList={manageButtonAccountProp}
                    shouldShowSpinner={false}
                    showDropdownOnTop={false}
                  />
                )}
                <CustomButton
                  type={'tertiary'}
                  className={classNames(
                    styles.viewAccountHistoryButton,
                    'button order-details-button'
                  )}
                  onClick={() => handleAccountHistoryClicked(account.id)}
                  isLoading={isAccountHistoryFetching}
                >
                  <FormattedMessage id="order-details-view-account-history-button-text" />
                </CustomButton>
              </div>
            </div>

            <div className={styles.section}>
              <div className={styles.sectionHeading}>
                <span>Flags</span>
              </div>
              <div className={styles.sectionData}>
                {account?.flags?.length ? (
                  <ul className="list-unstyled">
                    {account.flags.map((flag: string, index: number) => (
                      <li key={index}>
                        {flag} (<FormattedMessage id={`accountFlag_${flag}`} />)
                      </li>
                    ))}
                  </ul>
                ) : (
                  '-'
                )}
              </div>
            </div>

            <div className={styles.section}>
              <div className={styles.sectionHeading}>
                <span>Forwarders</span>
              </div>

              <div className={styles.sectionData}>
                {info?.sieveConfig?.forwarders?.length
                  ? info.sieveConfig.forwarders.map(
                      (forwarder: {
                        forwardingAddress: string;
                        keepCopy: boolean;
                      }) => {
                        return (
                          <div className={styles.forwarderRow}>
                            <div>{forwarder.forwardingAddress}</div>
                            <div>
                              {forwarder.keepCopy
                                ? 'Keeping a copy at Titan'
                                : 'Not keeping a copy at Titan'}
                            </div>
                          </div>
                        );
                      }
                    )
                  : '-'}
              </div>
            </div>
          </div>
        );
      });
    }
  }, [emailAccountsInfo, renderStatusIndicator]);

  return (
    <>
      <div className="order-details-column order-details-column-email">
        <div className="order-details-column-header">
          <div className="order-details-column-header-column">
            <div className="order-details-column-header-text">
              <FormattedMessage id="order-details-email-information" />
            </div>
            <div className="rectangle"></div>
          </div>
        </div>

        {isAccountsInfoFetching ? (
          <div className={styles.loadingSpinnerWrapper}>
            <LoadingSpinner />
          </div>
        ) : !isAccountsInfoFetching &&
          emailAccountsInfo &&
          emailAccountsInfo.length ? (
          <>{getEmailInfoToDisplay()}</>
        ) : (
          <div>
            <div className="no-mail-div">
              <div className="no-mail-logo">
                <NoMailContentLogo />
              </div>
              <div className="no-mail-content">
                <FormattedMessage
                  id="email-address-not-found"
                  values={{
                    email: queryText,
                    b: (word: string) => <b>{word}</b>,
                  }}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default BundleDetailsEmailInfo;
